import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  createUserAsync,
  googleCreateAsync,
  loginUserAsync,
} from "../authSlices";
import bg from "../image/image2.png";
import googlelogo from "../image/googlelogo.png";
import { useToast } from "@chakra-ui/react";
import "./Login.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { signInWithGoogle } from "../auth";
import { auth } from "../../../firebaseConfig";
import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "../../employerDashboard/AboutYou/AboutSlice";

const ScreenLoader = () => {
  return (
    <div className="loader-container">
      <div className="loader"></div>
    </div>
  );
};

export default function Signup() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const countries = useSelector(selectFetchCountry);
  const states = useSelector(selectFetchState);
  const cities = useSelector(selectFetchCities);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  useEffect(() => {
    const img = new Image();
    img.src = bg;
    img.onload = () => {
      setIsMounted(true);
    };
    dispatch(fetchCountryAsync());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCountry) {
      dispatch(fetchStateAsync(selectedCountry));
      setSelectedState("");
      setValue("state_uuid", "");
      setValue("city_uuid", "");
    }
  }, [selectedCountry, dispatch, setValue]);

  useEffect(() => {
    if (selectedState) {
      dispatch(fetchCitiesAsync(selectedState));
      setValue("city_uuid", "");
    }
  }, [selectedState, dispatch, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await dispatch(
        createUserAsync({
          name: data.name,
          email: data.email,
          password: data.password,
          phone: data.phone,
          c_password: data.c_password,
          user_type: "employer",
          country_id: data.country_uuid,
          state_id: data.state_uuid,
          city_id: data.city_uuid,
        })
      ).unwrap();

      try {
        await dispatch(
          loginUserAsync({ email: data.email, password: data.password })
        ).unwrap();
      } catch (loginErr) {
        console.warn("Auto-login failed after employer signup:", loginErr);
      }

      toast({
        title: "Signup successful",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Pass email and phone to phone verification page
      navigate("/phone-verification", {
        replace: true,
        state: {
          phone: data.phone,
          email: data.email,
          redirectTo: "/employer-basics",
          country_uuid: data.country_uuid,
          state_uuid: data.state_uuid,
          city_uuid: data.city_uuid
        },
      });

    } catch (e) {
      toast({
        title: "Error in Signup",
        description: e.message || "An error occurred during signup.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const { accessToken } = await signInWithGoogle();
      const payload = {
        accessToken,
        user_type: "employer",
      };

      const response = await dispatch(googleCreateAsync(payload)).unwrap();
      const loginType = response?.login_type;
      const userType = response?.user?.user_type;
      const phone = response?.user?.phone || "";
      const email = response?.user?.email || "";

      if (userType !== "employer") {
        toast({
          title: "Login Unsuccessful",
          description: "This is employer login only.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        await auth.signOut();
        return;
      }

      if (loginType === "login") {
        navigate("/employer-dashboard");
        toast({
          title: "Google login successful.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } else if (loginType === "register") {
        // Pass email and phone to phone verification page for Google signup too
        navigate("/phone-verification", {
          state: {
            phone,

            redirectTo: "/employer-basics",
            country_uuid: response.user?.country_id || "",
            state_uuid: response.user?.state_id || "",
            city_uuid: response.user?.city_id || "",
          }
        });
      }
    } catch (error) {
      toast({
        title: "Google login failed.",
        description: error.message || "There was an error logging in with Google.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.error("Google login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isMounted) {
    return <ScreenLoader />;
  }

  return (
    <div className="min-h-screen maindiv flex items-center justify-center px-2 py-4 font-p">
      {isLoading && <ScreenLoader />}
      <div className="flex flex-col md:flex-row bg-white shadow-lg rounded-lg overflow-hidden max-w-6xl w-full">
        <div className="w-full md:w-1/2 p-3 sm:p-4">
          <h1 className="text-left font-semibold text-2xl text-textBlue mb-0.5">IndiaHirings.com</h1>
          <p className="mt-1 text-left text-gray-600">
            Create your <span className="text-textBlue font-medium">Employer</span> Account to find top-tier talent.
          </p>

          <form className="mt-2" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 ">
              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input type="text" className={`w-full mt-1 px-2 py-2 text-sm border ${errors.name ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} placeholder="Enter your name" {...register("name", { required: "Name is required" })} />
                <div className="h-4">{errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}</div>
              </div>

              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail <span className="text-red-500">*</span></label>
                <input type="email" className={`w-full mt-1 px-2 py-2 text-sm border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} placeholder="Enter your email" {...register("email", { required: "Email is required", pattern: { value: /\b[\w.-]+@[\w.-]+\.\w{2,4}\b/gi, message: "Email is not valid" } })} />
                <div className="h-4">{errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}</div>
              </div>

              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                <div className="relative"><input type={showPassword ? "text" : "password"} className={`w-full mt-1 px-2 py-2 text-sm border ${errors.password ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} placeholder="Enter your password" {...register("password", { required: "Password is required", pattern: { value: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/, message: "Min. 6 chars, 1 uppercase, 1 number, 1 special char." } })} /><button type="button" className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 text-sm" onClick={togglePasswordVisibility}><FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} /></button></div>
                <div className="h-4">{errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}</div>
              </div>

              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password <span className="text-red-500">*</span></label>
                <div className="relative"><input type={showConfirmPassword ? "text" : "password"} className={`w-full mt-1 px-2 py-2 text-sm border ${errors.c_password ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} placeholder="Confirm your password" {...register("c_password", { required: "Confirm Password is required", validate: (value) => value === watch("password") || "Passwords do not match" })} /><button type="button" className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 text-sm" onClick={toggleConfirmPasswordVisibility}><FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} /></button></div>
                <div className="h-4">{errors.c_password && <p className="text-red-500 text-xs mt-1">{errors.c_password.message}</p>}</div>
              </div>

              <div className="mb-2  min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                <input type="text" className={`w-full mt-1 px-2 py-2 text-sm border ${errors.phone ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} placeholder="Enter your phone number" {...register("phone", { required: "Phone number is required", pattern: { value: /^\d{10}$/, message: "Phone number must be 10 digits" } })} />
                <div className="h-4">{errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}</div>
              </div>

              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Country <span className="text-red-500">*</span></label>
                <select className={`w-full mt-1 px-2 py-2 text-sm border ${errors.country_uuid ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} {...register("country_uuid", { required: "Country is required" })} onChange={(e) => { setSelectedCountry(e.target.value); setValue("country_uuid", e.target.value); }} value={selectedCountry}>
                  <option value="">Select Country</option>
                  {countries?.map((country) => (<option key={country.uuid} value={country.uuid}>{country.name}</option>))}
                </select>
                <div className="h-4">{errors.country_uuid && <p className="text-red-500 text-xs mt-1">{errors.country_uuid.message}</p>}</div>
              </div>

              <div className="mb-2 min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                <select className={`w-full mt-1 px-2 py-2 text-sm border ${errors.state_uuid ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} {...register("state_uuid", { required: "State is required" })} onChange={(e) => { setSelectedState(e.target.value); setValue("state_uuid", e.target.value); }} value={selectedState} disabled={!selectedCountry}>
                  <option value="">Select State</option>
                  {states?.map((state) => (<option key={state.uuid} value={state.uuid}>{state.name}</option>))}
                </select>
                <div className="h-4">{errors.state_uuid && <p className="text-red-500 text-xs mt-1">{errors.state_uuid.message}</p>}</div>
              </div>

              <div className="mb-2  min-h-[80px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                <select className={`w-full mt-1 px-2 py-2 text-sm border ${errors.city_uuid ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`} {...register("city_uuid", { required: "City is required" })} disabled={!selectedState}>
                  <option value="">Select City</option>
                  {cities?.map((city) => (<option key={city.uuid} value={city.uuid}>{city.name}</option>))}
                </select>
                <div className="h-4">{errors.city_uuid && <p className="text-red-500 text-xs mt-1">{errors.city_uuid.message}</p>}</div>
              </div>
            </div>

            <button type="submit" className="w-full mt-1 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition duration-300 shadow-sm hover:shadow-md" disabled={isLoading}>{isLoading ? "Signing up..." : "Sign Up"}</button>
            <div className="relative my-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div><div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-gray-500">OR</span></div></div>
            <button type="button" onClick={handleGoogleLogin} className="flex items-center justify-center py-2 px-4 bg-white hover:bg-gray-50 border border-gray-300 rounded-md w-full transition duration-300 shadow-sm hover:shadow-md" disabled={isLoading}><img src={googlelogo || "/placeholder.svg"} alt="Google Logo" className="h-6 w-6 object-cover" /><span className="ml-2">Sign Up with Google</span></button>
            <div className="text-center mt-3"><p className="text-sm text-gray-600">Already have an account?{" "}<Link to="/login" className="text-blue-600 font-medium hover:text-blue-800 hover:underline transition-colors text-sm">Log in</Link></p></div>
          </form>
        </div>

        <div className="hidden md:block md:w-1/2 relative">
          <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
          <img src={bg || "/placeholder.svg"} className="object-cover h-full w-full" alt="Background" />
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
            <div className="text-white max-w-xs">
              <h2 className="text-xl font-bold mb-2">Find Your Next Great Hire</h2>
              <p className="text-sm leading-relaxed">
                Connect with top-tier talent that matches your needs and aligns with your company's values on
                <span className="font-bold"> IndiaHirings.com</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}